import React, { Component } from 'react';
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';

/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      eventId: null 
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Store error info for display
    this.setState({ errorInfo });

    // Log to backend if available
    this.logErrorToService(error, errorInfo);
  }

  logErrorToService = async (error, errorInfo) => {
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const token = localStorage.getItem('authToken');
      
      await fetch(`${API}/errors/log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          error: error.toString(),
          stack: error.stack,
          componentStack: errorInfo?.componentStack,
          url: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        })
      }).catch(() => {
        // Silently fail if error logging fails
      });
    } catch (e) {
      // Silently fail
    }
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback, section } = this.props;

    if (hasError) {
      // Custom fallback provided
      if (fallback) {
        return fallback;
      }

      // Default error UI
      return (
        <div className="min-h-[400px] flex items-center justify-center p-4">
          <Card className="max-w-lg w-full border-red-200 bg-red-50/50 dark:bg-red-900/10 dark:border-red-800">
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <CardTitle className="text-red-700 dark:text-red-400">
                {section ? `Error in ${section}` : 'Something went wrong'}
              </CardTitle>
              <CardDescription className="text-red-600/80 dark:text-red-400/80">
                We encountered an unexpected error. Don't worry, your data is safe.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Error details (collapsible in production) */}
              {process.env.NODE_ENV === 'development' && error && (
                <details className="bg-white dark:bg-slate-800 rounded-lg p-3 border border-red-200 dark:border-red-800">
                  <summary className="cursor-pointer text-sm font-medium text-red-700 dark:text-red-400 flex items-center gap-2">
                    <Bug className="w-4 h-4" />
                    Error Details (Development Only)
                  </summary>
                  <div className="mt-2 space-y-2">
                    <p className="text-xs font-mono text-red-600 dark:text-red-400 break-all">
                      {error.toString()}
                    </p>
                    {errorInfo?.componentStack && (
                      <pre className="text-xs font-mono text-gray-600 dark:text-gray-400 overflow-auto max-h-32 bg-gray-50 dark:bg-slate-900 p-2 rounded">
                        {errorInfo.componentStack}
                      </pre>
                    )}
                  </div>
                </details>
              )}

              {/* Action buttons */}
              <div className="flex flex-col sm:flex-row gap-2">
                <Button 
                  onClick={this.handleRetry}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Try Again
                </Button>
                <Button 
                  variant="outline"
                  onClick={this.handleReload}
                  className="flex-1 border-red-300 text-red-700 hover:bg-red-100 dark:border-red-700 dark:text-red-400"
                >
                  Reload Page
                </Button>
                <Button 
                  variant="ghost"
                  onClick={this.handleGoHome}
                  className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-100"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Go Home
                </Button>
              </div>

              {/* Help text */}
              <p className="text-xs text-center text-gray-500 dark:text-gray-400">
                If this keeps happening, please contact support at{' '}
                <a href="mailto:builtbymarketing@gmail.com" className="text-purple-600 hover:underline">
                  builtbymarketing@gmail.com
                </a>
              </p>
            </CardContent>
          </Card>
        </div>
      );
    }

    return children;
  }
}

/**
 * Functional wrapper for easier use with hooks
 */
export const withErrorBoundary = (WrappedComponent, options = {}) => {
  const WithErrorBoundary = (props) => (
    <ErrorBoundary section={options.section} fallback={options.fallback}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );
  
  WithErrorBoundary.displayName = `withErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
  
  return WithErrorBoundary;
};

/**
 * Simple inline error boundary for smaller sections
 */
export const SectionErrorBoundary = ({ children, section, minimal = false }) => {
  if (minimal) {
    return (
      <ErrorBoundary 
        section={section}
        fallback={
          <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 text-center">
            <AlertTriangle className="w-5 h-5 text-red-500 mx-auto mb-2" />
            <p className="text-sm text-red-600 dark:text-red-400">
              Failed to load {section || 'this section'}
            </p>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => window.location.reload()}
              className="mt-2 text-red-600"
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              Reload
            </Button>
          </div>
        }
      >
        {children}
      </ErrorBoundary>
    );
  }
  
  return (
    <ErrorBoundary section={section}>
      {children}
    </ErrorBoundary>
  );
};

export default ErrorBoundary;
